import type {SliderKey} from '@hconnect/uikit/src/lib2/components/rangeSliders'
import type {Moment} from 'moment-timezone'
import React, {useContext} from 'react'

import {GANTT_CHART_STEPS_PER_HOUR} from '../../constants'
import type {MomentRange, NumberRange} from '../../types'
import {calculateRangeWithinBoundary, getLeftAlignedHourListFromRange} from '../../utils'

export interface PlanningChartStartEndState {
  startOfPlan: Moment
  endOfPlan: Moment
  startOfChart: Moment
  endOfChart: Moment
  boundary: MomentRange
  hoursList: Moment[]
  visibleHoursList: Moment[]
  roundRangeOnResize: (key: SliderKey, v: MomentRange) => MomentRange
  hoursRange: NumberRange
  setHoursRange: React.Dispatch<React.SetStateAction<NumberRange>>
  stepsPerHour: 1
  totalSteps: number
}

const PlanningChartStartEndContext = React.createContext<PlanningChartStartEndState | undefined>(
  undefined
)

export const PlanningChartRangeProvider = PlanningChartStartEndContext.Provider

interface ControlledPlanningChartStartEndProps {
  startOfPlan: Moment
  endOfPlan: Moment
  visibleRange: MomentRange
  onRangeChange: (range: MomentRange) => void
  children: React.ReactNode
}

export const ControlledPlanningChartStartEndProvider = ({
  startOfPlan,
  endOfPlan,
  visibleRange: [startOfChart, endOfChart],
  onRangeChange,
  children
}: ControlledPlanningChartStartEndProps): React.ReactElement => {
  const hoursList = getLeftAlignedHourListFromRange(startOfPlan, endOfPlan)
  const totalSteps = hoursList.length * GANTT_CHART_STEPS_PER_HOUR
  const hoursRange: NumberRange = [
    startOfChart.diff(startOfPlan, 'hours', true),
    endOfChart.diff(startOfPlan, 'hours', true)
  ]
  const setHoursRange: React.Dispatch<React.SetStateAction<NumberRange>> = (update) => {
    const next = calculateRangeWithinBoundary(
      typeof update === 'function' ? update(hoursRange) : update,
      [0, totalSteps]
    )
    onRangeChange([
      startOfPlan.clone().add(next[0], 'hours'),
      startOfPlan.clone().add(next[1], 'hours')
    ])
  }

  return (
    <PlanningChartRangeProvider
      value={{
        startOfPlan,
        endOfPlan,
        startOfChart,
        endOfChart,
        boundary: [startOfPlan, endOfPlan],
        hoursList,
        visibleHoursList: hoursList.slice(hoursRange[0], hoursRange[1]),
        hoursRange,
        setHoursRange,
        stepsPerHour: GANTT_CHART_STEPS_PER_HOUR,
        totalSteps,
        roundRangeOnResize: (_key, range) => range
      }}
    >
      {children}
    </PlanningChartRangeProvider>
  )
}

export const usePlanningChartStartEnd = (): PlanningChartStartEndState => {
  const context = useContext(PlanningChartStartEndContext)
  if (!context) {
    throw new Error(
      'Cannot use PlanningChartStartEndContext outside of a PlanningChartStartEndProvider'
    )
  }
  return context
}
